import { Link } from "react-router-dom";
import { ArrowRight, Users, Store } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { useWaitlist } from "@/context/WaitlistContext";
import { useTestFlight } from "@/context/TestFlightContext";

export default function TwoWays() {
  const { openWaitlist } = useWaitlist();
  const { openTestFlight, testFlightReady } = useTestFlight();

  const attendeeLabel = testFlightReady ? "Get the app" : "Get notified at launch";
  const onAttendeeClick = () => {
    if (testFlightReady) openTestFlight();
    else openWaitlist();
  };

  return (
    <section
      id="two-ways"
      className="relative py-24 md:py-28 overflow-hidden"
      data-testid="two-ways"
    >
      <div className="glow-orb w-[380px] h-[380px] bg-neon-violet/12 top-1/3 left-0" />
      <div className="glow-orb w-[380px] h-[380px] bg-neon-cyan/12 bottom-1/3 right-0" />

      <div className="container-x relative z-10">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="overline mb-4">Two ways in</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[0.95]">
            Are you going out — or <span className="text-gradient">hosting</span> the night?
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {/* Attendee rail */}
          <Reveal delay={0.05}>
            <div
              data-testid="two-ways-attendee"
              className="group relative h-full glass rounded-3xl p-8 md:p-10 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-neon-violet/20 blur-3xl opacity-70 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-2xl bg-neon-violet/20 text-neon-violet grid place-items-center mb-5 shadow-[0_0_28px_rgba(0,124,255,0.3)]">
                  <Users className="w-5 h-5" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-neon-cyan/80 font-medium mb-2">
                  For attendees
                </p>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                  I'm going out tonight.
                </h3>
                <p className="mt-4 text-zinc-400 leading-relaxed">
                  Check in when you arrive. See who's in the room. Say hi before the encore.
                  Free for everyone, everywhere.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-zinc-300">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan" />
                    Free to join · No credit card
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan" />
                    Concerts, sports, festivals, lounges
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan" />
                    Live on iOS & Android — venue partners start in DFW
                  </li>
                </ul>
                <button
                  onClick={onAttendeeClick}
                  data-testid="two-ways-attendee-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full h-12 px-6 text-sm font-semibold bg-gradient-to-r from-neon-violet to-neon-cyan text-white btn-glow border-0"
                >
                  {attendeeLabel}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Reveal>

          {/* Venue rail */}
          <Reveal delay={0.15}>
            <div
              data-testid="two-ways-venue"
              className="group relative h-full glass rounded-3xl p-8 md:p-10 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-neon-cyan/20 blur-3xl opacity-70 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-2xl bg-neon-cyan/20 text-neon-cyan grid place-items-center mb-5 shadow-[0_0_28px_rgba(0,198,174,0.3)]">
                  <Store className="w-5 h-5" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-neon-cyan/80 font-medium mb-2">
                  For venues
                </p>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                  I run the room.
                </h3>
                <p className="mt-4 text-zinc-400 leading-relaxed">
                  Give your guests a reason to remember each other — and to come back next week.
                  Founding Venues: free while we open DFW — nothing to buy yet.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-zinc-300">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan" />
                    Your venue's own check-in page — live in minutes
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan" />
                    Live guest counts + weekly digest
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan" />
                    No ads. No data resale. Your regulars stay yours.
                  </li>
                </ul>
                <a
                  href="/venues#apply"
                  data-testid="two-ways-venue-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full h-12 px-6 text-sm font-semibold bg-gradient-to-r from-neon-cyan to-neon-violet text-black hover:opacity-90 transition-opacity"
                >
                  Apply as a Founding Venue
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
